import React, { useState } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { SkeletonItem } from '@atlaskit/side-navigation';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ExpandableSubmenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-submenu-item/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useTMPSettingFeatures } from '../../../../controllers/features/main.tsx';
import { PROJECT_SETTINGS_SHIFTER_WEIGHT } from '../../../constants.tsx';
import { IssueTypes } from '../issue-types/index.tsx';
import commonMessages from '../messages.tsx';
import messages from './messages.tsx';

const requestTypesGroupWrapperStyles = xcss({
	paddingLeft: 'space.150',
});

const requestTypesWrapperStyles = xcss({
	paddingLeft: 'space.300',
});

type RequestsManagementMenuProps = {
	projectKey: string;
};
export const RequestsManagementMenu = ({ projectKey }: RequestsManagementMenuProps) => {
	const { formatMessage } = useIntl();
	const baseSettingsUrl = getNextgenSettingsUrl(SERVICE_DESK_PROJECT, projectKey);
	const tenantContext = useTenantContext();
	const { appPermissions } = tenantContext;
	const { hasServiceDeskAccess: hasJSD } = appPermissions;
	const route = useCurrentRoute();
	const { data: settingFeatures } = useTMPSettingFeatures();

	const calculateRequestTypesActive = ({ path }: { path: string }) =>
		/jira\/servicedesk\/projects\/.+\/settings\/issuetypes\/.+/.test(path);
	const [requestTypesActive] = useState<boolean>(calculateRequestTypesActive(route));

	if (settingFeatures == null || projectKey == null) return <SkeletonItem />;

	const [expConfig] = UNSAFE_noExposureExp('efa_and_project_fields_page_for_tmp');

	return (
		<>
			{hasJSD && (
				<Box xcss={requestTypesGroupWrapperStyles}>
					<ExpandableSubmenuItem
						analytics={{ itemId: 'request-types-group' }}
						menuItemLabel={formatMessage(messages.requestTypesGroup)}
						forceExpand={requestTypesActive}
						submenuId="request-types-group-settings"
					>
						<Box xcss={requestTypesWrapperStyles}>
							<IssueTypes />
						</Box>
					</ExpandableSubmenuItem>
				</Box>
			)}
			<MenuItem
				analytics={{ itemId: 'proforma-forms' }}
				selectedOn={['project-settings-servicedesk-proforma-project-forms']}
				href={`${baseSettingsUrl}/forms`}
			>
				{formatMessage(messages.forms)}
			</MenuItem>
			{hasJSD && (
				<>
					<MenuItem
						analytics={{ itemId: 'sla' }}
						selectedOn={['servicedesk-sla-settings']}
						href={`${baseSettingsUrl}/sla`}
						shifter={{
							groupTitle: formatMessage(messages.title),
							context: projectKey,
							weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
						}}
					>
						{formatMessage(messages.slaSettings)}
					</MenuItem>

					<MenuItem
						analytics={{ itemId: 'feedback-settings' }}
						selectedOn={['project-settings-servicedesk-feedback']}
						href={`${baseSettingsUrl}/feedback-settings`}
						shifter={{
							groupTitle: formatMessage(messages.title),
							context: projectKey,
							weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
						}}
					>
						{formatMessage(messages.customerSatisfaction)}
					</MenuItem>

					{settingFeatures?.hasFields && expConfig.get('isEnabled', false) && (
						<MenuItem
							analytics={{ itemId: 'fields' }}
							selectedOn={['project-settings-servicedesk-fields']}
							href={`${baseSettingsUrl}/fields`}
						>
							{formatMessage(commonMessages.fieldsNonFinal)}
						</MenuItem>
					)}
				</>
			)}
		</>
	);
};
